import { z } from 'zod';
import {
  ITEM_ROLES,
  LINK_TYPES,
  PRIORITIES,
  QUICK_TASK_CATEGORIES,
  SEVERITIES,
  STATUS_CATEGORIES,
} from '../enums';
import { ITEM_KEY_EXACT } from '../utils';
import { isoDate, richText, uuid } from './common';

export const itemKey = z.string().regex(ITEM_KEY_EXACT, 'Expected a work item key such as KBX-142');

export const rolesInput = z.object(
  Object.fromEntries(ITEM_ROLES.map((r) => [r, uuid.nullable().optional()])) as Record<
    (typeof ITEM_ROLES)[number],
    z.ZodOptional<z.ZodNullable<typeof uuid>>
  >,
);
export type RolesInput = z.infer<typeof rolesInput>;

const customFieldsInput = z.record(z.string(), z.unknown());

const itemFields = {
  title: z.string().trim().min(1).max(255),
  description: richText.nullable().optional(),
  priority: z.enum(PRIORITIES).optional(),
  severity: z.enum(SEVERITIES).nullable().optional(),
  assigneeId: uuid.nullable().optional(),
  componentId: uuid.nullable().optional(),
  labelIds: z.array(uuid).optional(),
  releaseId: uuid.nullable().optional(),
  sprintId: uuid.nullable().optional(),
  storyPoints: z.number().min(0).max(100).nullable().optional(),
  originalEstimateMin: z.number().int().min(0).nullable().optional(),
  remainingEstimateMin: z.number().int().min(0).nullable().optional(),
  /** Roadmap start (FR-PLN-7); mostly used on epics. */
  startDate: isoDate.nullable().optional(),
  dueDate: isoDate.nullable().optional(),
  customFields: customFieldsInput.optional(),
  // Bug fields (FR-WI-5)
  // Stored as rich-text JSONB columns, not only inside the description (FR-WI-5).
  stepsToReproduce: richText.nullable().optional(),
  expected: richText.nullable().optional(),
  actual: richText.nullable().optional(),
  environment: z.string().max(1000).nullable().optional(),
  foundInBuildId: uuid.nullable().optional(),
  fixedInBuildId: uuid.nullable().optional(),
  // Quick task fields (FR-QT-2)
  category: z.enum(QUICK_TASK_CATEGORIES).nullable().optional(),
  billable: z.boolean().optional(),
};

export const createItemSchema = z.object({
  type: z.string().min(1),
  parentKey: itemKey.nullable().optional(),
  roles: rolesInput.optional(),
  aiAssisted: z.boolean().optional(),
  aiRequestId: uuid.optional(),
  ...itemFields,
});
export type CreateItemInput = z.infer<typeof createItemSchema>;

export const updateItemSchema = z.object({
  version: z.number().int().min(1),
  parentKey: itemKey.nullable().optional(),
  type: z.string().min(1).optional(),
  ...itemFields,
  title: itemFields.title.optional(),
});
export type UpdateItemInput = z.infer<typeof updateItemSchema>;

/** Handoff note captured on key transitions (FR-WF-6). */
export const handoffSchema = z.object({
  whatChanged: z.string().min(1).max(5000),
  buildId: uuid.nullable().optional(),
  setup: z.string().max(5000).optional(),
  risk: z.string().max(2000).optional(),
});
export type HandoffInput = z.infer<typeof handoffSchema>;

export const transitionSchema = z.object({
  toStatusId: uuid,
  version: z.number().int().min(1).optional(),
  handoff: handoffSchema.optional(),
  fields: z
    .object({
      /** Required for Done-category transitions (FR-PRJ-3). */
      resolutionId: uuid.optional(),
      fixedInBuildId: uuid.optional(),
      assigneeId: uuid.optional(),
    })
    .partial()
    .optional(),
  comment: z.string().max(5000).optional(),
  /** Project Admin override of the "no open children" validator (§7.6). */
  overrideOpenChildren: z.boolean().optional(),
});
export type TransitionInput = z.infer<typeof transitionSchema>;

export const setRolesSchema = z.object({ roles: rolesInput });
export type SetRolesInput = z.infer<typeof setRolesSchema>;

export const createLinkSchema = z.object({ targetKey: itemKey, type: z.enum(LINK_TYPES) });
export type CreateLinkInput = z.infer<typeof createLinkSchema>;

export const rankSchema = z
  .object({
    /** Place the item immediately after this item (null = top). */
    afterKey: itemKey.nullable().optional(),
    /** Place the item immediately before this item (null = bottom). */
    beforeKey: itemKey.nullable().optional(),
    /**
     * Rank context (§7.3): `backlog` (project backlog order) or a sprint id (sprint order).
     * Defaults to the item's current context. Moving between contexts also changes the sprint.
     */
    context: z.union([z.literal('backlog'), uuid]).optional(),
    /** Move to a status (board drag across columns). */
    toStatusId: uuid.optional(),
  })
  .refine((v) => v.afterKey !== undefined || v.beforeKey !== undefined, {
    message: 'afterKey or beforeKey is required',
  });
export type RankInput = z.infer<typeof rankSchema>;

export const bulkUpdateSchema = z.object({
  keys: z.array(itemKey).min(1).max(500),
  set: z
    .object({
      assigneeId: uuid.nullable(),
      sprintId: uuid.nullable(),
      priority: z.enum(PRIORITIES),
      releaseId: uuid.nullable(),
      addLabelIds: z.array(uuid),
      removeLabelIds: z.array(uuid),
      toStatusId: uuid,
    })
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update'),
});
export type BulkUpdateInput = z.infer<typeof bulkUpdateSchema>;

const customFieldFilter = z.object({
  key: z.string(),
  op: z.enum(['eq', 'neq', 'contains', 'gt', 'lt', 'in', 'empty', 'not_empty']),
  value: z.unknown().optional(),
});

/** Advanced filter (FR-SRCH-2). Saved filters store this object as JSON. */
export const itemFilterSchema = z.object({
  projectKeys: z.array(z.string()).optional(),
  types: z.array(z.string()).optional(),
  statusIds: z.array(uuid).optional(),
  statusCategories: z.array(z.enum(STATUS_CATEGORIES)).optional(),
  /** user ids; the literal `me` resolves to the current user, `none` to unassigned. */
  assigneeIds: z.array(z.string()).optional(),
  reporterIds: z.array(z.string()).optional(),
  priorities: z.array(z.enum(PRIORITIES)).optional(),
  severities: z.array(z.enum(SEVERITIES)).optional(),
  releaseIds: z.array(z.string()).optional(),
  /** sprint ids; the literal `backlog` means no sprint. */
  sprintIds: z.array(z.string()).optional(),
  labelIds: z.array(uuid).optional(),
  componentIds: z.array(uuid).optional(),
  parentKey: itemKey.optional(),
  text: z.string().max(200).optional(),
  roleOwner: z.object({ role: z.enum(ITEM_ROLES), userId: z.string() }).optional(),
  unassignedRole: z.enum(ITEM_ROLES).optional(),
  customFields: z.array(customFieldFilter).optional(),
  dueBefore: isoDate.optional(),
  updatedSince: z.string().optional(),
  includeDeleted: z.boolean().optional(),
  sort: z
    .enum(['rank', 'updated_desc', 'created_desc', 'priority', 'due_date', 'key'])
    .default('updated_desc'),
});
export type ItemFilter = z.infer<typeof itemFilterSchema>;

/** Convert a Quick Task to a Story/Task/Bug (FR-QT-6); normal routing runs afterwards. */
export const convertQuickTaskSchema = z.object({
  version: z.number().int().min(1),
  type: z.enum(['story', 'task', 'bug']),
  releaseId: uuid.nullable().optional(),
  componentId: uuid.nullable().optional(),
  parentKey: itemKey.nullable().optional(),
});
export type ConvertQuickTaskInput = z.infer<typeof convertQuickTaskSchema>;

export const PRIORITY_ORDER: Record<string, number> = {
  highest: 1,
  high: 2,
  medium: 3,
  low: 4,
  lowest: 5,
};
